import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Users, TrendingUp, DollarSign, Trophy, Activity, Download, ShieldCheck, RefreshCw, ChevronLeft, ChevronRight, X, GitCompare, FileText, History, ChevronDown, Wrench, CheckCircle2, AlertTriangle, Search, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  getSeoDashboardStatus,
  validateJsonLd,
  getSeoCoverage,
  getSeoCoverageHistory,
  resubmitSitemap,
  getSeoAlertState,
  type GscStatus,
  type JsonLdPageReport,
  type SeoCoverageReport,
  type SeoCoverageSnapshot,
  type SitemapResubmitResult,
  type SeoAlertState,
} from "@/lib/seo-dashboard.functions";
import { AdminExtras } from "@/components/AdminExtras";
import { AdminEmailQueueTools } from "@/components/AdminEmailQueueTools";

import { AdminSecurityMemoryAudit } from "@/components/AdminSecurityMemoryAudit";
import { AdminTrialMetrics } from "@/components/AdminTrialMetrics";
import { AdminSubscriptionAnalytics } from "@/components/AdminSubscriptionAnalytics";
import { AdminBillingFunnel } from "@/components/AdminBillingFunnel";
import { AdminAcquisitionFunnel } from "@/components/AdminAcquisitionFunnel";
import { AdminMarketingExport } from "@/components/AdminMarketingExport";
import { AdminStudyMonetization } from "@/components/AdminStudyMonetization";

export const Route = createFileRoute("/admin")({
  component: AdminDashboard,
  head: () => ({
    meta: [
      { title: "Admin Dashboard — Inquira" },
      {
        name: "description",
        content:
          "Internal Inquira admin console for revenue metrics, subscriber growth, user moderation, support requests, security scans, and the audit log.",
      },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Admin Dashboard — Inquira" },
      {
        property: "og:description",
        content: "Internal Inquira admin console for revenue, moderation, security, and audit logs.",
      },
      { property: "og:url", content: "https://www.inquira.online/admin" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Admin Dashboard — Inquira" },
      {
        name: "twitter:description",
        content: "Internal Inquira admin console for revenue, moderation, security, and audit logs.",
      },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/admin" }],
  }),
});

interface Metrics {
  active_subscribers: number;
  mrr_usd: number;
  arr_usd: number;
  total_users: number;
  new_users_30d: number;
  new_subs_30d: number;
  conversion_rate_pct: number;
  signups_by_day: Array<{ date: string; count: number }>;
  subs_by_day: Array<{ date: string; count: number }>;
}

interface AuditLog {
  id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  actor_user_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface SavedReport {
  id: string;
  report: string;
  source: string;
  created_at: string;
}

/**
 * Registry of "known fixable" findings the one-click flow can address.
 * Each fix is a small, safe action the admin dashboard already knows how
 * to run (or verify) without a code deploy. New fixable findings are added
 * here as their remediation ships.
 */
type FixableFinding = {
  internal_id: string;
  title: string;
  detail: string;
  fix_label: string;
  /** Called when applying the fix. Returns the status message to display. */
  apply: () => Promise<string>;
};

interface FindingRow {
  internal_id: string;
  name: string;
  level: "warn" | "error" | "info" | string;
  description: string;
}

interface SecDefFn {
  function_name: string;
  args: string;
  anon_execute: boolean;
  authenticated_execute: boolean;
  public_execute: boolean;
}

interface FindingException {
  finding_key: string;
  reason: string;
  created_at: string;
}

const MILESTONES = [10, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];
const PRICE = 9.99;

function AdminDashboard() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [logsTotal, setLogsTotal] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // Audit log filters + pagination
  const [actionFilter, setActionFilter] = useState("");
  const [targetTypeFilter, setTargetTypeFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [logsLoading, setLogsLoading] = useState(false);

  // Security scan
  const [scanning, setScanning] = useState(false);
  const [scanReport, setScanReport] = useState<string | null>(null);
  const [previousReport, setPreviousReport] = useState<string | null>(null);
  const [previousGeneratedAt, setPreviousGeneratedAt] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [viewingReportId, setViewingReportId] = useState<string | null>(null);

  // Latest scanner findings + one-click safe-fix flow.
  const [findings, setFindings] = useState<FindingRow[] | null>(null);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [applyingFixes, setApplyingFixes] = useState(false);
  const [fixLog, setFixLog] = useState<string[]>([]);

  // SECURITY DEFINER function evidence + admin-marked exceptions
  const [secDefFns, setSecDefFns] = useState<SecDefFn[]>([]);
  const [exceptions, setExceptions] = useState<FindingException[]>([]);
  const [reasonDraft, setReasonDraft] = useState<Record<string, string>>({});
  const [markingKey, setMarkingKey] = useState<string | null>(null);

  // SEO dashboard
  const [gsc, setGsc] = useState<GscStatus | null>(null);
  const [gscLoading, setGscLoading] = useState(false);
  const [seoLastScanAt, setSeoLastScanAt] = useState<string | null>(
    typeof window !== "undefined" ? window.localStorage.getItem("seo:last_scan_at") : null,
  );
  const [seoAlert, setSeoAlert] = useState<SeoAlertState | null>(null);
  useEffect(() => {
    getSeoAlertState()
      .then(setSeoAlert)
      .catch(() => setSeoAlert(null));
  }, []);
  const [jsonLd, setJsonLd] = useState<{ pages: JsonLdPageReport[]; checkedAt: string } | null>(null);
  const [jsonLdLoading, setJsonLdLoading] = useState(false);
  const [jsonLdError, setJsonLdError] = useState<string | null>(null);

  // SEO health: Search Console coverage/indexing + snapshot history
  const [coverage, setCoverage] = useState<SeoCoverageReport | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState<string | null>(null);
  const [coverageHistory, setCoverageHistory] = useState<SeoCoverageSnapshot[]>([]);
  const [resubmit, setResubmit] = useState<SitemapResubmitResult | null>(null);
  const [resubmitLoading, setResubmitLoading] = useState(false);
  const [resubmitError, setResubmitError] = useState<string | null>(null);

  const runResubmit = useCallback(async () => {
    setResubmitLoading(true);
    setResubmitError(null);
    try {
      const r = await resubmitSitemap({
        data: { paths: ["/algebra-ai-solver", "/calculus-ai-solver"] },
      });
      setResubmit(r);
    } catch (e) {
      setResubmitError(e instanceof Error ? e.message : "Sitemap re-submission failed");
    } finally {
      setResubmitLoading(false);
    }
  }, []);

  const loadCoverageHistory = useCallback(async () => {
    try {
      setCoverageHistory(await getSeoCoverageHistory());
    } catch {
      /* non-fatal */
    }
  }, []);

  const runCoverageCheck = useCallback(async () => {
    setCoverageLoading(true);
    setCoverageError(null);
    try {
      const r = await getSeoCoverage();
      setCoverage(r);
      await loadCoverageHistory();
    } catch (e) {
      setCoverageError(e instanceof Error ? e.message : "Coverage check failed");
    } finally {
      setCoverageLoading(false);
    }
  }, [loadCoverageHistory]);

  const SEO_PATHS = ["/", "/upgrade", "/calculus-ai-solver", "/algebra-ai-solver", "/practice", "/history"];

  const loadGsc = useCallback(async () => {
    setGscLoading(true);
    try {
      const s = await getSeoDashboardStatus();
      setGsc(s);
    } finally {
      setGscLoading(false);
    }
  }, []);

  const runJsonLdCheck = useCallback(async () => {
    setJsonLdLoading(true);
    setJsonLdError(null);
    try {
      const r = await validateJsonLd({ data: { paths: SEO_PATHS } });
      setJsonLd(r);
      const now = new Date().toISOString();
      setSeoLastScanAt(now);
      if (typeof window !== "undefined") window.localStorage.setItem("seo:last_scan_at", now);
    } catch (e) {
      setJsonLdError(e instanceof Error ? e.message : "Validation failed");
    } finally {
      setJsonLdLoading(false);
    }
  }, []);

  /**
   * Resolve the actor filter to a list of user_ids to match against
   * audit_logs.actor_user_id. Supports raw UUIDs and email substring
   * matches (profiles.display_name is seeded from email on signup).
   */
  const resolveActorIds = useCallback(async (raw: string): Promise<string[] | null> => {
    const q = raw.trim();
    if (!q) return null;
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(q);
    if (isUuid) return [q];
    const { data } = await supabase
      .from("profiles")
      .select("user_id")
      .ilike("display_name", `%${q}%`)
      .limit(500);
    return (data ?? []).map((r) => (r as { user_id: string }).user_id);
  }, []);

  const buildLogsQuery = useCallback((actorIds: string[] | null) => {
    let q = supabase
      .from("audit_logs" as never)
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false });
    if (actionFilter.trim()) q = q.ilike("action", `%${actionFilter.trim()}%`);
    if (targetTypeFilter.trim()) q = q.ilike("target_type", `%${targetTypeFilter.trim()}%`);
    if (fromDate) q = q.gte("created_at", new Date(fromDate).toISOString());
    if (toDate) {
      const to = new Date(toDate);
      to.setDate(to.getDate() + 1);
      q = q.lt("created_at", to.toISOString());
    }
    if (actorIds !== null) {
      if (actorIds.length === 0) {
        // No matches — return a query guaranteed to produce zero rows.
        q = q.eq("actor_user_id", "00000000-0000-0000-0000-000000000000");
      } else {
        q = q.in("actor_user_id", actorIds);
      }
    }
    return q;
  }, [actionFilter, targetTypeFilter, fromDate, toDate]);

  const resolvedActorIds = useCallback(async () => {
    if (!actorFilter.trim()) return null;
    return resolveActorIds(actorFilter);
  }, [actorFilter, resolveActorIds]);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    const from = page * pageSize;
    const to = from + pageSize - 1;
    const ids = await resolvedActorIds();
    const { data, count, error: e } = await buildLogsQuery(ids).range(from, to);
    if (e) setError(e.message);
    if (data) setLogs(data as unknown as AuditLog[]);
    setLogsTotal(count ?? 0);
    setLogsLoading(false);
  }, [buildLogsQuery, page, pageSize, resolvedActorIds]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    (async () => {
      const { data: roleOk } = await supabase.rpc("has_role" as never, {
        _user_id: user.id,
        _role: "super_admin",
      } as never);
      if (roleOk !== true) {
        setIsAdmin(false);
        setChecking(false);
        return;
      }
      setIsAdmin(true);
      const { data: m, error: mErr } = await supabase.rpc("admin_metrics" as never);
      if (mErr) setError(mErr.message);
      else setMetrics(m as unknown as Metrics);
      setChecking(false);
    })();
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (isAdmin) loadLogs();
  }, [isAdmin, loadLogs]);

  // Reset to first page whenever filters change
  useEffect(() => { setPage(0); }, [actionFilter, targetTypeFilter, fromDate, toDate, actorFilter]);

  const loadSavedReports = useCallback(async () => {
    const { data } = await supabase
      .from("security_reports" as never)
      .select("id, report, source, created_at")
      .order("created_at", { ascending: false })
      .limit(10);
    setSavedReports((data ?? []) as unknown as SavedReport[]);
  }, []);

  useEffect(() => {
    if (isAdmin) loadSavedReports();
  }, [isAdmin, loadSavedReports]);

  useEffect(() => {
    if (isAdmin) {
      loadGsc();
      loadCoverageHistory();
    }
  }, [isAdmin, loadGsc]);

  const rowsToCsv = (rows: AuditLog[]) => {
    const header = ["timestamp", "action", "target_type", "target_id", "actor_user_id", "metadata"];
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const body = rows.map((log) =>
      [
        new Date(log.created_at).toISOString(),
        log.action,
        log.target_type ?? "",
        log.target_id ?? "",
        log.actor_user_id ?? "",
        JSON.stringify(log.metadata ?? {}),
      ]
        .map((c) => escape(String(c)))
        .join(",")
    );
    return [header.join(","), ...body].join("\n");
  };

  const downloadCsv = (csv: string, name: string) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCurrentPageCsv = () => {
    downloadCsv(
      rowsToCsv(logs),
      `inquira-audit-log-page${page + 1}-${new Date().toISOString().split("T")[0]}.csv`,
    );
  };

  const exportAllFilteredCsv = async () => {
    // Page through all matching rows (no 500-row cap).
    const chunk = 1000;
    let offset = 0;
    let all: AuditLog[] = [];
    // Safety cap to avoid runaway downloads
    const MAX = 100000;
    const ids = await resolvedActorIds();
    while (offset < MAX) {
      const { data, error: e } = await buildLogsQuery(ids).range(offset, offset + chunk - 1);
      if (e) { setError(e.message); return; }
      const batch = (data ?? []) as unknown as AuditLog[];
      all = all.concat(batch);
      if (batch.length < chunk) break;
      offset += chunk;
    }
    downloadCsv(rowsToCsv(all), `inquira-audit-log-${new Date().toISOString().split("T")[0]}.csv`);
  };

  /**
   * The security-scan function requires a validated Supabase session. The
   * generated client can invoke before its session is rehydrated, which the
   * function rejects with "missing_authorization_header" (401). Resolve the
   * session first and attach the bearer token explicitly.
   */
  const invokeSecurityScan = async (body?: Record<string, unknown>) => {
    const { data: sessionData } = await supabase.auth.getSession();
    let token = sessionData.session?.access_token;
    if (!token) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      token = refreshed.session?.access_token;
    }
    if (!token) {
      throw new Error("Your session expired — sign in again to run a security scan.");
    }
    return supabase.functions.invoke("security-scan", {
      headers: { Authorization: `Bearer ${token}` },
      ...(body ? { body } : {}),
    });
  };

  const runSecurityScan = async () => {
    setScanning(true);
    setScanError(null);
    setScanReport(null);
    setPreviousReport(null);
    setShowDiff(false);
    try {
      const { data, error: fnErr } = await invokeSecurityScan();
      if (fnErr) throw fnErr;
      const payload = data as {
        report?: string;
        previous_report?: string | null;
        previous_generated_at?: string | null;
        findings?: FindingRow[];
      } | null;
      const report = payload?.report;
      if (!report) throw new Error("No report returned");
      setScanReport(report);
      setPreviousReport(payload?.previous_report ?? null);
      setPreviousGeneratedAt(payload?.previous_generated_at ?? null);
      if (Array.isArray(payload?.findings)) setFindings(payload!.findings!);
      // Refresh the saved-report list so the new scan shows up immediately.
      loadSavedReports();
    } catch (e) {
      setScanError(e instanceof Error ? e.message : "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  /**
   * Registry of safe, one-click fixes. Each entry knows how to verify itself
   * against the current database state; the applier runs them in order and
   * then triggers a fresh scan so the admin sees the resulting delta.
   */
  const fixables: FixableFinding[] = [
    {
      internal_id: "rate_limits_no_policies",
      title: "rate_limits table has no RLS policies",
      detail:
        "Adds owner-scoped SELECT plus RESTRICTIVE deny policies for INSERT/UPDATE/DELETE. Writes continue to flow through the SECURITY DEFINER `check_rate_limit` RPC, which bypasses RLS.",
      fix_label: "Verify RLS policies",
      apply: async () => {
        // Verify by attempting a scoped SELECT — succeeds under the new SELECT policy.
        const { error } = await supabase
          .from("rate_limits" as never)
          .select("id", { head: true, count: "exact" })
          .limit(1);
        if (error) throw new Error(`rate_limits select failed: ${error.message}`);
        return "rate_limits: owner-scoped SELECT + RESTRICTIVE write-deny policies verified.";
      },
    },
  ];

  const applySafeFixesAndRescan = async () => {
    setApplyingFixes(true);
    setFixLog([]);
    const log: string[] = [];
    for (const f of fixables) {
      try {
        const msg = await f.apply();
        log.push(`✅ ${f.internal_id}: ${msg}`);
      } catch (e) {
        log.push(`❌ ${f.internal_id}: ${e instanceof Error ? e.message : "failed"}`);
      }
      setFixLog([...log]);
    }
    setApplyingFixes(false);
    await runSecurityScan();
  };

  const loadFindings = useCallback(async () => {
    setFindingsLoading(true);
    try {
      const { data } = await invokeSecurityScan({ findingsOnly: true });
      const payload = data as { findings?: FindingRow[] } | null;
      if (Array.isArray(payload?.findings)) setFindings(payload!.findings!);
    } catch {
      /* findings are a best-effort hydration; the Re-run button surfaces errors */
    } finally {
      setFindingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin && findings === null) loadFindings();
  }, [isAdmin, findings, loadFindings]);

  const loadSecDefEvidence = useCallback(async () => {
    const { data } = await supabase.rpc("admin_security_definer_functions" as never);
    if (Array.isArray(data)) setSecDefFns(data as unknown as SecDefFn[]);
  }, []);

  const loadExceptions = useCallback(async () => {
    const { data } = await supabase
      .from("security_finding_exceptions" as never)
      .select("finding_key, reason, created_at")
      .order("created_at", { ascending: false });
    setExceptions((data ?? []) as unknown as FindingException[]);
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadSecDefEvidence();
      loadExceptions();
    }
  }, [isAdmin, loadSecDefEvidence, loadExceptions]);

  const markIntentional = async (key: string) => {
    const reason = (reasonDraft[key] ?? "").trim();
    if (!reason || !user) return;
    setMarkingKey(key);
    const { error: insErr } = await supabase
      .from("security_finding_exceptions" as never)
      .insert({ finding_key: key, reason, created_by: user.id } as never);
    setMarkingKey(null);
    if (insErr) { setScanError(insErr.message); return; }
    setReasonDraft((d) => ({ ...d, [key]: "" }));
    await loadExceptions();
  };

  const unmarkIntentional = async (key: string) => {
    const { error: delErr } = await supabase
      .from("security_finding_exceptions" as never)
      .delete()
      .eq("finding_key", key);
    if (delErr) { setScanError(delErr.message); return; }
    await loadExceptions();
  };

  const downloadScanReport = () => {
    if (!scanReport) return;
    const blob = new Blob([scanReport], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inquira-security-report-${new Date().toISOString().split("T")[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (authLoading || checking) {
    return (
      <div className="min-h-svh grid place-items-center text-ink-muted text-sm">Loading admin…</div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-svh grid place-items-center px-6">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-ink mb-2">Not authorized</h1>
          <p className="text-ink-muted text-sm mb-4">This page is restricted to Inquira super admins.</p>
          <Link to="/" className="text-neon-pink font-semibold text-sm inline-flex items-center gap-1">
            <ArrowLeft className="size-4" /> Back to Inquira
          </Link>
        </div>
      </div>
    );
  }

  const nextMilestone =
    metrics && MILESTONES.find((m) => m > metrics.active_subscribers);
  const prevMilestone =
    metrics &&
    [...MILESTONES].reverse().find((m) => m <= metrics.active_subscribers);
  const progressPct =
    metrics && nextMilestone
      ? Math.min(
          100,
          ((metrics.active_subscribers - (prevMilestone ?? 0)) /
            (nextMilestone - (prevMilestone ?? 0))) *
            100
        )
      : 100;

  const maxSignups = metrics ? Math.max(1, ...metrics.signups_by_day.map((d) => d.count)) : 1;
  const maxSubs = metrics ? Math.max(1, ...metrics.subs_by_day.map((d) => d.count)) : 1;

  return (
    <div className="min-h-svh bg-bg text-ink">
      <header className="border-b border-rule px-4 sm:px-6 py-3 flex items-center justify-between">
        <Link to="/" className="text-sm font-semibold text-ink-muted hover:text-ink inline-flex items-center gap-1">
          <ArrowLeft className="size-4" /> Back
        </Link>
        <h1 className="text-lg font-extrabold font-[family-name:var(--font-display)]">
          Admin<span className="text-neon-pink">.</span>
        </h1>
        <Link
          to="/analytics-health"
          className="text-sm font-semibold text-ink-muted hover:text-ink inline-flex items-center gap-1"
        >
          <Activity className="size-4" /> Health
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        {error && (
          <div className="rounded-lg border border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-300 p-3 text-sm">
            {error}
          </div>
        )}

        {metrics && (
          <>
            {/* Metric cards */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard icon={<DollarSign className="size-4" />} label="MRR" value={`$${metrics.mrr_usd}`} sub={`ARR $${metrics.arr_usd}`} />
              <MetricCard icon={<Users className="size-4" />} label="Subscribers" value={metrics.active_subscribers.toLocaleString()} sub={`+${metrics.new_subs_30d} in 30d`} />
              <MetricCard icon={<Activity className="size-4" />} label="Total users" value={metrics.total_users.toLocaleString()} sub={`+${metrics.new_users_30d} in 30d`} />
              <MetricCard icon={<TrendingUp className="size-4" />} label="Conversion" value={`${metrics.conversion_rate_pct}%`} sub="Paid / Total" />
            </section>

            {/* Milestones */}
            <section className="rounded-2xl border border-rule bg-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="size-4 text-neon-pink" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-ink">Subscriber milestones</h2>
              </div>
              {nextMilestone ? (
                <>
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-2xl font-extrabold text-ink">
                      {metrics.active_subscribers.toLocaleString()}
                    </span>
                    <span className="text-xs text-ink-muted">
                      Next: {nextMilestone.toLocaleString()} · projected MRR ${(nextMilestone * PRICE).toFixed(0)}
                    </span>
                  </div>
                  <div className="h-3 rounded-full bg-muted overflow-hidden mb-4">
                    <div
                      className="h-full bg-gradient-to-r from-neon-pink to-neon-violet transition-all"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-ink-muted mb-4">All defined milestones cleared 🎉</p>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {MILESTONES.map((m) => {
                  const reached = metrics.active_subscribers >= m;
                  return (
                    <div
                      key={m}
                      className={`rounded-lg border px-2 py-2 text-center text-xs ${
                        reached
                          ? "border-neon-pink/50 bg-neon-pink/10 text-neon-pink font-bold"
                          : "border-rule text-ink-muted"
                      }`}
                    >
                      <div>{m.toLocaleString()}</div>
                      <div className="opacity-70">${(m * PRICE).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Charts */}
            <section className="grid md:grid-cols-2 gap-4">
              <ChartCard title="Signups (30d)" data={metrics.signups_by_day} max={maxSignups} color="bg-neon-violet" />
              <ChartCard title="New subscriptions (30d)" data={metrics.subs_by_day} max={maxSubs} color="bg-neon-pink" />
            </section>
          </>
        )}

        {/* Churn, retention & feature usage */}
        <GrowthSection />

        {/* Security scan */}
        <section className="rounded-2xl border border-rule bg-card p-5">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="text-sm font-bold uppercase tracking-wider text-ink flex items-center gap-2">
              <ShieldCheck className="size-4 text-neon-pink" /> Security scan
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={runSecurityScan}
                disabled={scanning}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-neon-pink px-3 py-1.5 rounded-full disabled:opacity-50"
              >
                <RefreshCw className={`size-3.5 ${scanning ? "animate-spin" : ""}`} />
                {scanning ? "Scanning…" : "Re-run security scan"}
              </button>
              {scanReport && (
                <button
                  onClick={downloadScanReport}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-neon-pink hover:text-neon-violet transition-colors"
                >
                  <Download className="size-3.5" /> Download report
                </button>
              )}
            </div>
          </div>
          {scanError && (
            <p className="text-sm text-destructive mb-2">{scanError}</p>
          )}

          {/* Latest scanner findings */}
          <div className="mb-4 rounded-lg border border-rule bg-bg/60 p-3">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <h3 className="text-xs font-bold uppercase tracking-wider text-ink-muted flex items-center gap-1.5">
                <AlertTriangle className="size-3.5" /> Latest scanner findings
                {findings && (
                  <span className="text-[10px] normal-case font-semibold text-ink">
                    ({findings.length})
                  </span>
                )}
              </h3>
              <button
                onClick={applySafeFixesAndRescan}
                disabled={applyingFixes || scanning}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-neon-violet px-3 py-1.5 rounded-full disabled:opacity-50"
                title="Apply the known safe fixes registered here, then re-run the security scan"
              >
                <Wrench className={`size-3.5 ${applyingFixes ? "animate-pulse" : ""}`} />
                {applyingFixes ? "Applying…" : "Apply safe fixes & re-scan"}
              </button>
            </div>

            {findingsLoading ? (
              <p className="text-xs text-ink-muted">Loading findings…</p>
            ) : !findings ? (
              <p className="text-xs text-ink-muted">Run a scan to load findings.</p>
            ) : (() => {
              // Enrich generic SECURITY DEFINER findings with per-function evidence.
              const secDefFindings = findings.filter(
                (f) => f.internal_id === "SUPA_authenticated_security_definer_function_executable",
              );
              const otherFindings = findings.filter(
                (f) => f.internal_id !== "SUPA_authenticated_security_definer_function_executable",
              );
              const authFns = secDefFns.filter((s) => s.authenticated_execute);
              const enriched = secDefFindings.map((f, idx) => {
                const fn = authFns[idx];
                const key = fn
                  ? `secdef:${fn.function_name}(${fn.args})`
                  : `${f.internal_id}#${idx}`;
                return { f, fn, key };
              });
              const exceptionMap = new Map(exceptions.map((e) => [e.finding_key, e]));
              const activeCount =
                otherFindings.length +
                enriched.filter(({ key }) => !exceptionMap.has(key)).length;
              const totalCount = findings.length;

              return (
                <>
                  <p className="text-[11px] text-ink-muted mb-2">
                    {activeCount === 0 ? (
                      <span className="inline-flex items-center gap-1.5 text-neon-violet">
                        <CheckCircle2 className="size-3.5" /> No active findings — {totalCount - activeCount} marked intentional.
                      </span>
                    ) : (
                      <>
                        <span className="font-semibold text-ink">{activeCount}</span> active
                        {totalCount - activeCount > 0 && (
                          <> · {totalCount - activeCount} marked intentional</>
                        )}
                      </>
                    )}
                  </p>

                  <ul className="space-y-1.5">
                    {otherFindings.map((f, i) => (
                      <li key={`other-${i}`} className="text-xs text-ink border border-rule rounded p-2 bg-card">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-semibold">{f.name}</span>
                          <span className="text-[10px] uppercase tracking-wider text-neon-pink">{f.level}</span>
                        </div>
                        <p className="text-ink-muted mt-1">{f.description}</p>
                        <p className="text-[10px] text-ink-muted mt-1 font-mono">id: {f.internal_id}</p>
                      </li>
                    ))}

                    {enriched.map(({ f, fn, key }) => {
                      const exc = exceptionMap.get(key);
                      return (
                        <li
                          key={key}
                          className={`text-xs border rounded p-2 ${
                            exc
                              ? "border-neon-violet/40 bg-neon-violet/5 text-ink-muted"
                              : "border-rule bg-card text-ink"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="font-semibold">
                              {fn ? `${fn.function_name}(${fn.args || ""})` : f.name}
                            </span>
                            <span className={`text-[10px] uppercase tracking-wider ${
                              exc ? "text-neon-violet" : "text-neon-pink"
                            }`}>
                              {exc ? "intentional" : f.level}
                            </span>
                          </div>
                          {fn && (
                            <div className="mt-1.5 flex flex-wrap gap-1 text-[10px]">
                              <span className={`px-1.5 py-0.5 rounded font-mono ${fn.anon_execute ? "bg-red-500/20 text-red-500" : "bg-muted text-ink-muted"}`}>
                                anon: {fn.anon_execute ? "EXECUTE" : "denied"}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded font-mono ${fn.authenticated_execute ? "bg-amber-500/20 text-amber-600 dark:text-amber-400" : "bg-muted text-ink-muted"}`}>
                                authenticated: {fn.authenticated_execute ? "EXECUTE" : "denied"}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded font-mono ${fn.public_execute ? "bg-red-500/20 text-red-500" : "bg-muted text-ink-muted"}`}>
                                public: {fn.public_execute ? "EXECUTE" : "denied"}
                              </span>
                            </div>
                          )}
                          <p className="text-ink-muted mt-1">{f.description}</p>
                          <p className="text-[10px] text-ink-muted mt-1 font-mono">
                            id: {f.internal_id} · key: {key}
                          </p>

                          {exc ? (
                            <div className="mt-2 flex items-start justify-between gap-2 flex-wrap">
                              <div className="text-[11px]">
                                <span className="font-semibold text-ink">Reason:</span> {exc.reason}
                                <span className="block text-[10px] text-ink-muted mt-0.5">
                                  marked {new Date(exc.created_at).toLocaleString()}
                                </span>
                              </div>
                              <button
                                onClick={() => unmarkIntentional(key)}
                                className="text-[11px] font-semibold text-neon-pink hover:underline"
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <input
                                type="text"
                                value={reasonDraft[key] ?? ""}
                                onChange={(e) =>
                                  setReasonDraft((d) => ({ ...d, [key]: e.target.value }))
                                }
                                placeholder="Why is this intentional?"
                                className="flex-1 min-w-[160px] text-[11px] bg-bg border border-rule rounded px-2 py-1 text-ink"
                              />
                              <button
                                onClick={() => markIntentional(key)}
                                disabled={markingKey === key || !(reasonDraft[key] ?? "").trim()}
                                className="text-[11px] font-semibold text-white bg-neon-violet px-2.5 py-1 rounded-full disabled:opacity-50"
                              >
                                {markingKey === key ? "Saving…" : "Mark intentional"}
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </>
              );
            })()}

            {fixLog.length > 0 && (
              <pre className="text-[11px] font-mono text-ink whitespace-pre-wrap bg-muted rounded p-2 mt-2 max-h-40 overflow-auto">
                {fixLog.join("\n")}
              </pre>
            )}

            <details className="mt-2">
              <summary className="text-[11px] text-ink-muted cursor-pointer">
                Known fixable findings ({fixables.length})
              </summary>
              <ul className="mt-1 space-y-1">
                {fixables.map((f) => (
                  <li key={f.internal_id} className="text-[11px] text-ink-muted">
                    <span className="font-mono">{f.internal_id}</span> — {f.title}
                  </li>
                ))}
              </ul>
            </details>
          </div>

          {scanReport ? (
            <>
              {previousReport && (
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <button
                    onClick={() => setShowDiff((v) => !v)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-neon-violet hover:text-neon-pink transition-colors"
                  >
                    <GitCompare className="size-3.5" />
                    {showDiff ? "Hide comparison" : "Compare with last report"}
                  </button>
                  {previousGeneratedAt && (
                    <span className="text-xs text-ink-muted">
                      previous: {new Date(previousGeneratedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              )}
              {showDiff && previousReport ? (
                <DiffView oldText={previousReport} newText={scanReport} />
              ) : (
                <pre className="text-xs text-ink whitespace-pre-wrap font-mono bg-muted rounded-lg p-3 max-h-80 overflow-auto">
                  {scanReport}
                </pre>
              )}
            </>
          ) : (
            <p className="text-sm text-ink-muted">
              Regenerate a detailed report of RLS coverage, SECURITY DEFINER exposure, and role-based access.
            </p>
          )}

          {/* Saved reports (nightly + manual runs) */}
          {savedReports.length > 0 && (
            <div className="mt-4 border-t border-rule pt-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-ink-muted mb-2 flex items-center gap-1.5">
                <History className="size-3.5" /> Recent scans
              </h3>
              <ul className="space-y-1">
                {savedReports.map((r) => (
                  <li key={r.id}>
                    <button
                      onClick={() => {
                        setViewingReportId(viewingReportId === r.id ? null : r.id);
                      }}
                      className="w-full flex items-center justify-between gap-2 text-xs text-ink hover:text-neon-pink transition-colors py-1"
                    >
                      <span className="flex items-center gap-1.5">
                        <FileText className="size-3.5 text-ink-muted" />
                        {new Date(r.created_at).toLocaleString()}
                        <span className="text-[10px] uppercase tracking-wider text-ink-muted">
                          {r.source}
                        </span>
                      </span>
                      <ChevronDown className={`size-3.5 transition-transform ${viewingReportId === r.id ? "rotate-180" : ""}`} />
                    </button>
                    {viewingReportId === r.id && (
                      <pre className="text-xs text-ink whitespace-pre-wrap font-mono bg-muted rounded-lg p-3 max-h-64 overflow-auto mt-1">
                        {r.report}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Audit log */}
        <section className="rounded-2xl border border-rule bg-card p-5">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="text-sm font-bold uppercase tracking-wider text-ink">
              Audit log{" "}
              <span className="text-ink-muted font-normal normal-case">
                (page {page + 1} · {logsTotal.toLocaleString()} total)
              </span>
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={exportCurrentPageCsv}
                disabled={logs.length === 0}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-muted hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="size-3.5" /> Export current page ({logs.length})
              </button>
              <button
                onClick={exportAllFilteredCsv}
                disabled={logsTotal === 0}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-neon-pink hover:text-neon-violet disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="size-3.5" /> Export {logsTotal.toLocaleString()} rows to CSV
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            <input
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="Filter by action…"
              className="rounded-lg border border-rule bg-bg px-3 py-1.5 text-sm text-ink placeholder:text-ink-muted"
            />
            <input
              value={targetTypeFilter}
              onChange={(e) => setTargetTypeFilter(e.target.value)}
              placeholder="Target type…"
              className="rounded-lg border border-rule bg-bg px-3 py-1.5 text-sm text-ink placeholder:text-ink-muted"
            />
            <input
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              placeholder="Actor (user id or email)…"
              className="rounded-lg border border-rule bg-bg px-3 py-1.5 text-sm text-ink placeholder:text-ink-muted"
            />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-lg border border-rule bg-bg px-3 py-1.5 text-sm text-ink"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-lg border border-rule bg-bg px-3 py-1.5 text-sm text-ink"
            />
          </div>
          {(actionFilter || targetTypeFilter || actorFilter || fromDate || toDate) && (
            <button
              onClick={() => { setActionFilter(""); setTargetTypeFilter(""); setActorFilter(""); setFromDate(""); setToDate(""); }}
              className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink mb-3"
            >
              <X className="size-3" /> Clear filters
            </button>
          )}

          {logsLoading ? (
            <p className="text-sm text-ink-muted">Loading…</p>
          ) : logs.length === 0 ? (
            <p className="text-sm text-ink-muted">No admin actions recorded for this filter.</p>
          ) : (
            <ul className="divide-y divide-rule">
              {logs.map((log) => (
                <li key={log.id} className="py-2 text-sm flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-ink">{log.action}</p>
                    <p className="text-xs text-ink-muted">
                      {log.target_type ?? "-"} {log.target_id ? `· ${log.target_id}` : ""}
                    </p>
                  </div>
                  <time className="text-xs text-ink-muted whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString()}
                  </time>
                </li>
              ))}
            </ul>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || logsLoading}
              className="inline-flex items-center gap-1 text-xs font-semibold text-ink-muted hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="size-3.5" /> Prev
            </button>
            <span className="text-xs text-ink-muted">
              {logs.length > 0
                ? `${page * pageSize + 1}–${page * pageSize + logs.length} of ${logsTotal.toLocaleString()}`
                : ""}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={logsLoading || (page + 1) * pageSize >= logsTotal}
              className="inline-flex items-center gap-1 text-xs font-semibold text-ink-muted hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="size-3.5" />
            </button>
          </div>
        </section>

        {/* ==================== SEO Dashboard ==================== */}
        <section className="rounded-2xl border border-rule bg-card p-4 md:p-6 mt-6">
          <div className="flex items-center gap-2 mb-4">
            <Search className="size-4 text-neon-blue" />
            <h2 className="font-[family-name:var(--font-display)] font-extrabold text-lg text-ink">
              SEO Dashboard
            </h2>
          </div>

          {/* Status tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="rounded-xl border border-rule p-3">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted font-bold mb-1">
                Last JSON-LD scan
              </div>
              <div className="text-sm font-semibold text-ink">
                {seoLastScanAt ? new Date(seoLastScanAt).toLocaleString() : "Never run"}
              </div>
              {jsonLd && (
                <div className="text-xs text-ink-muted mt-1">
                  {jsonLd.pages.length} pages · {jsonLd.pages.reduce((a, p) => a + p.blockCount, 0)} blocks
                </div>
              )}
            </div>
            <div className="rounded-xl border border-rule p-3">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted font-bold mb-1">
                Scheduled crawl-error alerts
              </div>
              {seoAlert ? (
                <>
                  <div className="text-sm font-semibold text-ink">
                    {seoAlert.consecutiveIncreases}/{seoAlert.threshold} consecutive increases
                  </div>
                  <div className="text-xs text-ink-muted mt-1">
                    Last check {seoAlert.lastCheckedAt ? new Date(seoAlert.lastCheckedAt).toLocaleString() : "never"}
                    {" · "}last alert {seoAlert.lastAlertAt ? new Date(seoAlert.lastAlertAt).toLocaleString() : "none"}
                    {" · "}Slack {seoAlert.slackConfigured ? "on" : "off"}
                  </div>
                </>
              ) : (
                <div className="text-xs text-ink-muted">Not available</div>
              )}
            </div>
            <div className="rounded-xl border border-rule p-3">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted font-bold mb-1">
                Google Search Console
              </div>
              {gscLoading ? (
                <div className="text-sm text-ink-muted">Checking…</div>
              ) : gsc?.connected ? (
                <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                  Connected · {gsc.siteCount} verified {gsc.siteCount === 1 ? "property" : "properties"}
                </div>
              ) : (
                <div className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                  Not connected
                </div>
              )}
              {gsc?.error && (
                <div className="text-[11px] text-ink-muted mt-1 line-clamp-2">{gsc.error}</div>
              )}
              {gsc?.sites?.length ? (
                <ul className="text-[11px] text-ink-muted mt-1 space-y-0.5">
                  {gsc.sites.slice(0, 3).map((s) => (
                    <li key={s.siteUrl} className="truncate">• {s.siteUrl}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={runJsonLdCheck}
              disabled={jsonLdLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-neon-blue text-paper px-3 py-1.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              <RefreshCw className={`size-3.5 ${jsonLdLoading ? "animate-spin" : ""}`} />
              {jsonLdLoading ? "Scanning…" : "Re-run JSON-LD scan"}
            </button>
            <button
              onClick={loadGsc}
              disabled={gscLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-rule px-3 py-1.5 text-sm font-semibold text-ink hover:bg-rule/30 disabled:opacity-50"
            >
              <RefreshCw className={`size-3.5 ${gscLoading ? "animate-spin" : ""}`} />
              Refresh GSC status
            </button>
            <a
              href="https://search.google.com/search-console"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border border-rule px-3 py-1.5 text-sm font-semibold text-ink hover:bg-rule/30"
            >
              Open Search Console <ExternalLink className="size-3.5" />
            </a>
          </div>

          {jsonLdError && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg p-2 mb-3">
              {jsonLdError}
            </div>
          )}

          {jsonLd && (
            <div className="space-y-2">
              {jsonLd.pages.map((p) => {
                const errCount = p.blocks.filter((b) => !b.ok).length;
                const warnCount = p.blocks.reduce((a, b) => a + b.warnings.length, 0);
                const healthy = errCount === 0 && warnCount === 0 && p.blockCount > 0 && !p.pageError;
                return (
                  <div key={p.path} className="rounded-xl border border-rule p-3">
                    <div className="flex items-center gap-2 mb-1">
                      {healthy ? (
                        <CheckCircle2 className="size-4 text-emerald-600" />
                      ) : errCount > 0 || p.pageError ? (
                        <AlertTriangle className="size-4 text-destructive" />
                      ) : (
                        <AlertTriangle className="size-4 text-amber-600" />
                      )}
                      <code className="text-sm font-semibold text-ink">{p.path}</code>
                      <span className="text-xs text-ink-muted ml-auto">
                        {p.blockCount} block{p.blockCount === 1 ? "" : "s"} · HTTP {p.status || "n/a"}
                      </span>
                    </div>
                    {p.pageError && (
                      <p className="text-xs text-destructive">Fetch error: {p.pageError}</p>
                    )}
                    {p.blockCount === 0 && !p.pageError && (
                      <p className="text-xs text-amber-600">No JSON-LD found on this page.</p>
                    )}
                    <ul className="text-xs space-y-0.5 mt-1">
                      {p.blocks.map((b) => (
                        <li key={b.index} className={b.ok ? "text-ink-muted" : "text-destructive"}>
                          #{b.index + 1}: {b.ok ? `✓ ${b.type ?? "unknown"}` : `✗ ${b.error}`}
                          {b.warnings.map((w, i) => (
                            <div key={i} className="text-amber-600 pl-4">⚠ {w}</div>
                          ))}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}

          {!jsonLd && !jsonLdLoading && (
            <p className="text-xs text-ink-muted">
              Click <strong>Re-run JSON-LD scan</strong> to parse and validate schema blocks across all key routes.
            </p>
          )}
        </section>

        <section className="rounded-2xl border border-rule bg-card p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Search className="size-4 text-neon-blue" />
            <h2 className="font-bold text-ink">SEO health — Search Console coverage</h2>
            <button
              onClick={runCoverageCheck}
              disabled={coverageLoading}
              className="ml-auto inline-flex items-center gap-2 rounded-lg bg-neon-blue text-paper px-3 py-1.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              <RefreshCw className={`size-3.5 ${coverageLoading ? "animate-spin" : ""}`} />
              {coverageLoading ? "Checking…" : "Check coverage"}
            </button>
            <button
              onClick={runResubmit}
              disabled={resubmitLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-rule px-3 py-1.5 text-sm font-semibold text-ink hover:bg-rule/30 disabled:opacity-50"
            >
              <RefreshCw className={`size-3.5 ${resubmitLoading ? "animate-spin" : ""}`} />
              {resubmitLoading ? "Submitting…" : "Re-submit sitemap & re-check pages"}
            </button>
          </div>

          {coverageError && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg p-2 mb-3">
              {coverageError}
            </div>
          )}
          {resubmitError && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg p-2 mb-3">
              {resubmitError}
            </div>
          )}
          {resubmit && (
            <div className="rounded-xl border border-rule p-3 mb-3 text-xs text-ink-muted space-y-1">
              <div className="font-bold text-ink">
                {resubmit.sitemapResubmitted
                  ? `Sitemap re-submitted: ${resubmit.sitemapUrl}`
                  : `Sitemap submission failed: ${resubmit.sitemapError ?? "unknown error"}`}
              </div>
              {resubmit.inspected.map((p) => (
                <div key={p.path} className="flex items-center gap-2">
                  <span className="font-mono">{p.path}</span>
                  <span>· {p.error ?? `${p.verdict} — ${p.coverageState}`}</span>
                  {p.inspectionLink && (
                    <a
                      href={p.inspectionLink}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-neon-blue"
                    >
                      Request indexing <ExternalLink className="size-3" />
                    </a>
                  )}
                </div>
              ))}
              <div className="pt-1">
                Google has no public API to force indexing of standard pages — open the inspection
                link above and click <strong>Request Indexing</strong> to finish.
              </div>
            </div>
          )}
          {coverage?.fatal && (
            <div className="text-sm text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 mb-3">
              {coverage.fatal}
            </div>
          )}

          {coverage && !coverage.fatal && (
            <>
              {coverage.errorDelta > 0 && (
                <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-3 mb-3 flex items-start gap-2">
                  <AlertTriangle className="size-4 text-destructive mt-0.5" />
                  <div className="text-sm text-destructive">
                    <strong>Coverage errors increased</strong> — {coverage.previousErrorCount} →{" "}
                    {coverage.errors.length} (+{coverage.errorDelta}) since the last check. Review the
                    sitemap/robots issues below.
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <MetricCard
                  icon={<CheckCircle2 className="size-3.5" />}
                  label="Indexed pages"
                  value={`${coverage.pagesIndexed}/${coverage.pagesChecked}`}
                  sub={coverage.siteUrl ?? undefined}
                />
                <MetricCard
                  icon={<AlertTriangle className="size-3.5" />}
                  label="Coverage issues"
                  value={String(coverage.errors.length)}
                  sub={
                    coverage.newErrors.length
                      ? `${coverage.newErrors.length} new · Δ${coverage.errorDelta >= 0 ? "+" : ""}${coverage.errorDelta}`
                      : `no new errors · Δ${coverage.errorDelta >= 0 ? "+" : ""}${coverage.errorDelta}`
                  }
                />
                <MetricCard
                  icon={<FileText className="size-3.5" />}
                  label="Sitemaps"
                  value={String(coverage.sitemaps.length)}
                  sub={`${coverage.sitemaps.reduce((a, s) => a + s.submittedUrls, 0)} URLs submitted`}
                />
                <MetricCard
                  icon={<History className="size-3.5" />}
                  label="Last checked"
                  value={new Date(coverage.checkedAt).toLocaleTimeString()}
                  sub={new Date(coverage.checkedAt).toLocaleDateString()}
                />
              </div>

              {coverage.newErrors.length > 0 && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 mb-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-destructive mb-1">
                    New since last check
                  </div>
                  <ul className="text-xs text-destructive space-y-0.5">
                    {coverage.newErrors.map((e, i) => (
                      <li key={i}>• {e}</li>
                    ))}
                  </ul>
                </div>
              )}

              {coverage.sitemaps.length > 0 && (
                <ul className="text-xs text-ink-muted space-y-1 mb-3">
                  {coverage.sitemaps.map((s) => (
                    <li key={s.path} className="truncate">
                      <span className="text-ink font-semibold">{s.path}</span> —{" "}
                      {s.errors > 0 ? `${s.errors} errors` : "no errors"}, {s.warnings} warnings,{" "}
                      {s.indexedUrls}/{s.submittedUrls} indexed
                      {s.lastDownloaded
                        ? ` · downloaded ${new Date(s.lastDownloaded).toLocaleDateString()}`
                        : " · not downloaded yet"}
                    </li>
                  ))}
                </ul>
              )}

              <div className="space-y-1.5 mb-4">
                {coverage.pages.map((p) => (
                  <div
                    key={p.path}
                    className="flex items-center gap-2 rounded-lg border border-rule px-3 py-2"
                  >
                    {p.indexed ? (
                      <CheckCircle2 className="size-4 text-emerald-600 shrink-0" />
                    ) : (
                      <AlertTriangle className="size-4 text-amber-600 shrink-0" />
                    )}
                    <code className="text-sm font-semibold text-ink">{p.path}</code>
                    <span className="text-xs text-ink-muted ml-auto text-right truncate">
                      {p.coverageState}
                    </span>
                    {p.inspectionLink && (
                      <a
                        href={p.inspectionLink}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Open ${p.path} in Search Console`}
                        className="text-ink-muted hover:text-ink"
                      >
                        <ExternalLink className="size-3.5" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {coverageHistory.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted font-bold mb-1">
                History
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-ink-muted">
                    <tr className="text-left">
                      <th className="py-1 pr-3 font-semibold">When</th>
                      <th className="py-1 pr-3 font-semibold">Indexed</th>
                      <th className="py-1 pr-3 font-semibold">Issues</th>
                      <th className="py-1 pr-3 font-semibold">Sitemap errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coverageHistory.map((s) => (
                      <tr key={s.id} className="border-t border-rule">
                        <td className="py-1 pr-3 text-ink-muted">
                          {new Date(s.created_at).toLocaleString()}
                        </td>
                        <td className="py-1 pr-3 text-ink">
                          {s.pages_indexed}/{s.pages_checked}
                        </td>
                        <td className="py-1 pr-3 text-ink">{(s.errors ?? []).length}</td>
                        <td className="py-1 pr-3 text-ink">{s.sitemap_errors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!coverage && coverageHistory.length === 0 && !coverageLoading && (
            <p className="text-xs text-ink-muted">
              Click <strong>Check coverage</strong> to pull live indexing status from Google Search
              Console and record a snapshot.
            </p>
          )}
        </section>
        <AdminAcquisitionFunnel />
        <AdminMarketingExport />
        <AdminBillingFunnel />
        <AdminTrialMetrics />
        <AdminSubscriptionAnalytics />
        <AdminStudyMonetization />
        <AdminSecurityMemoryAudit />
        <AdminEmailQueueTools />
        <AdminExtras />

      </main>
    </div>
  );
}

function MetricCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl border border-rule bg-card p-4">
      <div className="flex items-center gap-2 text-ink-muted text-xs mb-1">
        {icon}
        <span className="uppercase tracking-wider font-bold">{label}</span>
      </div>
      <div className="text-2xl font-extrabold text-ink">{value}</div>
      {sub && <div className="text-xs text-ink-muted mt-0.5">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, data, max, color }: { title: string; data: Array<{ date: string; count: number }>; max: number; color: string }) {
  return (
    <div className="rounded-2xl border border-rule bg-card p-5">
      <h3 className="text-sm font-bold uppercase tracking-wider text-ink mb-3">{title}</h3>
      <div className="flex items-end gap-[2px] h-32">
        {data.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col justify-end" title={`${d.date}: ${d.count}`}>
            <div
              className={`${color} rounded-t opacity-80 hover:opacity-100 transition-opacity`}
              style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? "2px" : "0" }}
            />
          </div>
        ))}
      </div>
      <p className="text-xs text-ink-muted mt-2">Last 30 days · total {data.reduce((s, d) => s + d.count, 0)}</p>
    </div>
  );
}

/**
 * Minimal line-level diff view for security scan reports. Highlights removed
 * lines in red and added lines in green. Uses a simple set-difference —
 * good enough for short markdown reports (report bodies rarely exceed a few
 * dozen lines and are stable in structure).
 */
function DiffView({ oldText, newText }: { oldText: string; newText: string }) {
  const oldLines = oldText.split("\n");
  const newLines = newText.split("\n");
  const oldSet = new Set(oldLines);
  const newSet = new Set(newLines);
  const removed = oldLines.filter((l) => !newSet.has(l));
  const added = newLines.filter((l) => !oldSet.has(l));
  return (
    <div className="grid md:grid-cols-2 gap-2">
      <div className="rounded-lg bg-muted p-3 max-h-80 overflow-auto">
        <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1 font-bold">Removed</div>
        {removed.length === 0 ? (
          <p className="text-xs text-ink-muted">No lines removed.</p>
        ) : (
          <pre className="text-xs font-mono text-destructive whitespace-pre-wrap">
            {removed.map((l, i) => `- ${l}`).join("\n")}
          </pre>
        )}
      </div>
      <div className="rounded-lg bg-muted p-3 max-h-80 overflow-auto">
        <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1 font-bold">Added</div>
        {added.length === 0 ? (
          <p className="text-xs text-ink-muted">No new lines.</p>
        ) : (
          <pre className="text-xs font-mono text-neon-violet whitespace-pre-wrap">
            {added.map((l, i) => `+ ${l}`).join("\n")}
          </pre>
        )}
      </div>
    </div>
  );
}
interface GrowthMetrics {
  active_subscribers: number;
  churned_30d: number;
  churn_rate_pct: number;
  retention_rate_pct: number;
  dau: number;
  wau: number;
  mau: number;
  feature_usage_30d: Record<string, number>;
  referrals: { total: number; converted: number; converted_30d: number };
  affiliates: {
    approved: number;
    pending: number;
    conversions_30d: number;
    commission_owed_cents: number;
  };
  generated_at: string;
}

const FEATURE_LABELS: Record<string, string> = {
  problems_solved: "Problems solved",
  flashcards_created: "Flashcards created",
  flashcard_reviews: "Flashcard reviews",
  quizzes_created: "Quizzes created",
  quiz_attempts: "Quiz attempts",
  study_goals: "Study plans",
  feedback_items: "Feedback items",
};

/**
 * Churn / retention / active-user / feature-usage panel. Backed by the
 * admin-only `admin_growth_metrics` RPC (role checked server-side).
 */
function GrowthSection() {
  const [data, setData] = useState<GrowthMetrics | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    supabase.rpc("admin_growth_metrics" as never).then(({ data: d, error }) => {
      if (error) setErr(error.message);
      else setData(d as unknown as GrowthMetrics);
    });
  }, []);

  return (
    <section className="rounded-2xl border border-rule bg-card p-5">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-ink">
        <TrendingUp className="size-4 text-neon-violet" />
        Churn, retention &amp; feature usage
      </h2>
      {err && <p className="text-sm text-destructive">{err}</p>}
      {!data && !err && <p className="text-sm text-ink-muted">Loading…</p>}
      {data && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <MetricCard icon={<TrendingUp className="size-4" />} label="Churn (30d)" value={`${data.churn_rate_pct}%`} sub={`${data.churned_30d} canceled`} />
            <MetricCard icon={<Activity className="size-4" />} label="Retention" value={`${data.retention_rate_pct}%`} sub="Rolling 30 days" />
            <MetricCard icon={<Users className="size-4" />} label="DAU / WAU" value={`${data.dau} / ${data.wau}`} sub={`MAU ${data.mau}`} />
            <MetricCard icon={<Trophy className="size-4" />} label="Referral conversions" value={`${data.referrals.converted}`} sub={`${data.referrals.converted_30d} in 30d`} />
          </div>

          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">Feature usage (30d)</h3>
            <ul className="divide-y divide-rule text-sm">
              {Object.entries(data.feature_usage_30d).map(([key, value]) => (
                <li key={key} className="flex justify-between py-1.5">
                  <span>{FEATURE_LABELS[key] ?? key}</span>
                  <span className="font-mono">{value.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">Affiliates</h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <MetricCard icon={<Users className="size-4" />} label="Approved" value={`${data.affiliates.approved}`} sub={`${data.affiliates.pending} pending`} />
              <MetricCard icon={<Activity className="size-4" />} label="Conversions (30d)" value={`${data.affiliates.conversions_30d}`} sub="Paid invoices" />
              <MetricCard icon={<DollarSign className="size-4" />} label="Commission owed" value={`$${(data.affiliates.commission_owed_cents / 100).toFixed(2)}`} sub="Unpaid" />
              <MetricCard icon={<Trophy className="size-4" />} label="Referrals total" value={`${data.referrals.total}`} sub="All time" />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
